import { Box, SxProps, useTheme } from '@mui/material';
import { DragEvent, PropsWithChildren, useCallback, useRef, useState } from 'react';

type DroppableProps = {
  onDragEnter?: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (event: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (event: DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: DragEvent<HTMLDivElement>) => void;
  dragOverSx?: SxProps;
  sx?: SxProps;
};

export const Droppable = ({
  children,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  dragOverSx,
  sx,
}: PropsWithChildren<DroppableProps>) => {
  const { palette } = useTheme();
  const dragCounterRef = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      dragCounterRef.current += 1;

      // Only fire the parent onDragEnter when we first enter the container
      if (dragCounterRef.current === 1) {
        setIsDragOver(true);
        if (onDragEnter) {
          onDragEnter(event);
        }
      }
    },
    [onDragEnter],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      dragCounterRef.current -= 1;

      // Only fire the parent onDragLeave when we've completely left the container
      if (dragCounterRef.current === 0) {
        setIsDragOver(false);
        if (onDragLeave) {
          onDragLeave(event);
        }
      }
    },
    [onDragLeave],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (onDragOver) {
        onDragOver(event);
      }
    },
    [onDragOver],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // Reset counter on drop
      dragCounterRef.current = 0;
      setIsDragOver(false);

      if (onDrop) {
        onDrop(event);
      }
    },
    [onDrop],
  );

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={
        [
          isDragOver && {
            border: `2px dashed ${palette.primary.main}`,
          },
          isDragOver && dragOverSx,
          sx,
        ] as SxProps
      }
    >
      {children}
    </Box>
  );
};
